import {
    ApolloClient,
    InMemoryCache,
    from,
    ApolloLink,
    type NormalizedCacheObject,
} from '@apollo/client';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { setContext } from '@apollo/client/link/context';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { onError } from '@apollo/client/link/error';
import {
    API_BASE_URI,
    OAUTH2_CLIENT_ID,
    OAUTH2_CLIENT_SECRET,
    getOAuth2Store,
    setOAuth2Store,
    removeOAuth2Store,
} from '@/store';
import { apolloClientRef } from './apolloClientRef';
import { isPrerenderRun } from '@/utils/prerender/prerenderCoordinator';

type Response = {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    token_type: 'Bearer';
};

export const createApolloClient = (
    setAccessToken: (token: string | null) => void,
    initialState?: NormalizedCacheObject,
    shouldHydrate: boolean = true,
) => {
    // Purges dead credentials and notifies React state
    const purgeStaleAuth = () => {
        removeOAuth2Store();
        if (apolloClientRef.accessToken !== null) {
            apolloClientRef.accessToken = null;
            if (typeof setAccessToken === 'function') {
                setAccessToken(null);
            }
        }
    };

    // 1. Catches any 401 or Unauthenticated GraphQL/Network errors
    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
            for (const err of graphQLErrors) {
                if (
                    err.extensions?.code === 'UNAUTHENTICATED' ||
                    err.message?.toLowerCase().includes('unauthorized')
                ) {
                    purgeStaleAuth();
                }
            }
        }
        if (
            networkError &&
            'statusCode' in networkError &&
            networkError.statusCode === 401
        ) {
            purgeStaleAuth();
        }
    });

    const authLink = setContext((_, { headers }) => {
        const token = apolloClientRef.accessToken;
        const oAuth2Object = getOAuth2Store();

        return {
            headers: {
                ...headers,
                authorization: token
                    ? `${oAuth2Object?.tokenType || 'Bearer'} ${token}`
                    : '',
            },
        };
    });

    const refreshLink = new TokenRefreshLink({
        isTokenValidOrUndefined: async () => {
            const oAuth2Object = getOAuth2Store();
            if (!apolloClientRef.accessToken) return true;

            if (!oAuth2Object?.expires || !oAuth2Object?.refreshToken)
                return true;

            return oAuth2Object.expires > Date.now() + 30_000;
        },

        fetchAccessToken: async () => {
            const oAuth2Object = getOAuth2Store();

            if (!oAuth2Object?.refreshToken) {
                return {
                    access_token: '',
                    expires_in: 0,
                    refresh_token: '',
                    token_type: 'Bearer',
                };
            }

            try {
                const response = await fetch(`${API_BASE_URI}/oauth/token`, {
                    method: 'POST',
                    body: new URLSearchParams({
                        grant_type: 'refresh_token',
                        refresh_token: oAuth2Object.refreshToken,
                        client_id: OAUTH2_CLIENT_ID,
                        client_secret: OAUTH2_CLIENT_SECRET,
                    }),
                });

                if (!response.ok) {
                    purgeStaleAuth();
                    return {
                        access_token: '',
                        expires_in: 0,
                        refresh_token: '',
                        token_type: 'Bearer',
                    };
                }

                return await response.json();
            } catch (err) {
                purgeStaleAuth();
                throw err;
            }
        },

        handleFetch: (newAccessToken) => {
            setAccessToken(newAccessToken);
            apolloClientRef.accessToken = newAccessToken;
        },

        handleResponse: () => (response: Response) => {
            if (!response?.access_token) {
                purgeStaleAuth();
                return '';
            }

            setOAuth2Store(
                response.access_token,
                response.refresh_token,
                response.expires_in,
                response.token_type,
            );
            apolloClientRef.accessToken = response.access_token;
            return response.access_token;
        },

        handleError: (error) => {
            console.error('Cannot refresh access token:', error);
            purgeStaleAuth();
        },
    });

    const httpLink = createUploadLink({
        uri: `${API_BASE_URI}/graphql`,
        headers: { 'Apollo-Require-Preflight': 'true' },
    });

    const cache = new InMemoryCache().restore(
        shouldHydrate ? initialState || {} : {},
    );

    const isPrerendering = typeof window === 'undefined' || isPrerenderRun();

    const links = isPrerendering
        ? [httpLink as unknown as ApolloLink]
        : [errorLink, refreshLink, authLink, httpLink as unknown as ApolloLink];

    return new ApolloClient({
        link: from(links),
        cache,
    });
};
